import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays,
  ClipboardCheck,
  Flag,
  ListChecks,
  MapPinned,
  Rows3,
  Settings,
  UsersRound,
} from 'lucide-react';
import type {
  Assignment,
  CompetitionDay,
  Entry,
  Event,
  EventReadiness,
  Jump,
  Level,
  Volunteer,
} from '@wht-xc-utils/shared/domain';

import { EventTabs } from '../components/EventTabs';
import { api } from '../lib/api';

export function EventHome() {
  const { eventId } = useParams<{ eventId: string }>();

  const event = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get<{ event: Event }>(`/api/events/${eventId}`),
    enabled: !!eventId,
  });

  const days = useQuery({
    queryKey: ['days', eventId],
    queryFn: () => api.get<{ days: CompetitionDay[] }>(`/api/events/${eventId}/days`),
    enabled: !!eventId,
  });

  const levels = useQuery({
    queryKey: ['levels', eventId],
    queryFn: () => api.get<{ levels: Level[] }>(`/api/events/${eventId}/levels`),
    enabled: !!eventId,
  });

  const entries = useQuery({
    queryKey: ['entries', eventId],
    queryFn: () => api.get<{ entries: Entry[] }>(`/api/events/${eventId}/entries`),
    enabled: !!eventId,
  });

  const volunteers = useQuery({
    queryKey: ['volunteers', eventId],
    queryFn: () => api.get<{ volunteers: Volunteer[] }>(`/api/events/${eventId}/volunteers`),
    enabled: !!eventId,
  });

  const jumps = useQuery({
    queryKey: ['jumps', eventId],
    queryFn: () => api.get<{ jumps: Jump[] }>(`/api/events/${eventId}/jumps`),
    enabled: !!eventId,
  });

  const assignments = useQuery({
    queryKey: ['assignments', eventId],
    queryFn: () => api.get<{ assignments: Assignment[] }>(`/api/events/${eventId}/assignments`),
    enabled: !!eventId,
  });
  const readiness = useQuery({
    queryKey: ['eventReadiness', eventId],
    queryFn: () => api.get<{ readiness: EventReadiness }>(`/api/events/${eventId}/readiness`),
    enabled: !!eventId,
  });
  const nextStep = getNextStep(readiness.data?.readiness, eventId);
  const eventDays = days.data?.days ?? [];
  const dateLabel =
    eventDays.length > 0
      ? eventDays
          .slice()
          .sort((a, b) => a.dayOrder - b.dayOrder)
          .map((day) => day.label || day.date)
          .join(' · ')
      : 'Dates will appear after schedule setup';

  return (
    <>
      <EventTabs />

      <section className="event-home-hero">
        <div>
          <span className="eyebrow">Cross country operations</span>
          <h2>{event.data?.event.name ?? 'Event planning'}</h2>
          <p>
            Build the competition schedule, organize judging posts, and place volunteers with a
            clear view of coverage.
          </p>
          <div className="event-hero-meta">
            <span>
              <CalendarDays aria-hidden="true" />
              {dateLabel}
            </span>
            <span>
              <Rows3 aria-hidden="true" />
              {event.data?.event.scoreSheetRows ?? '—'} rows per score sheet
            </span>
          </div>
        </div>
        <Flag aria-hidden="true" className="event-home-hero-icon" />
      </section>

      <section className="dashboard-grid" aria-label="Event totals">
        <Metric icon={CalendarDays} label="Competition days" value={eventDays.length} />
        <Metric icon={ListChecks} label="Entries" value={entries.data?.entries.length} />
        <Metric icon={UsersRound} label="Volunteers" value={volunteers.data?.volunteers.length} />
        <Metric icon={MapPinned} label="Jumps" value={jumps.data?.jumps.length} />
        <Metric
          icon={ClipboardCheck}
          label="Assignments"
          value={assignments.data?.assignments.length}
        />
        <Metric icon={Flag} label="Levels" value={levels.data?.levels.length} />
      </section>

      <div className="card next-step-card">
        <div>
          <span className="eyebrow">Recommended next step</span>
          <h2>{nextStep.title}</h2>
          <p className="muted">{nextStep.description}</p>
        </div>

        <div className="workflow-actions">
          <Link className="button-link primary" to={nextStep.to}>
            {nextStep.action}
          </Link>
        </div>
      </div>

      <div className="card next-step-card">
        <div>
          <span className="eyebrow">Administration</span>
          <h2>Settings, history, and data management</h2>
          <p className="muted">
            Configure packet details or review and restore prior event versions.
          </p>
        </div>
        <div className="workflow-actions">
          <Link className="button-link subtle" to={`/events/${eventId}/admin`}>
            <Settings aria-hidden="true" />
            Open administration
          </Link>
        </div>
      </div>
    </>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof CalendarDays;
  label: string;
  value: number | undefined;
}) {
  return (
    <article className="metric-card">
      <span className="metric-card-icon" aria-hidden="true">
        <Icon />
      </span>
      <span className="metric-card-value">{value ?? '—'}</span>
      <span className="metric-card-label">{label}</span>
    </article>
  );
}

function getNextStep(readiness: EventReadiness | undefined, eventId: string | undefined) {
  const base = `/events/${eventId}`;
  if (!readiness?.entriesReady) {
    return {
      title: 'Link and review entries',
      description:
        'Import the ShowConnect entry list and resolve any division mappings before scheduling.',
      action: 'Continue to entries',
      to: `${base}/entries`,
    };
  }
  if (!readiness.scheduleReady) {
    return {
      title: 'Review the running schedule',
      description: 'Confirm competition days and division order derived from rider times.',
      action: 'Continue to schedule',
      to: `${base}/schedule`,
    };
  }
  if (!readiness.volunteersReady) {
    return {
      title: 'Import volunteer shifts',
      description: 'Add the current volunteer workbook so jump-judge coverage can be planned.',
      action: 'Continue to volunteers',
      to: `${base}/volunteers`,
    };
  }
  if (!readiness.jumpsReady) {
    return {
      title: 'Map courses and judging posts',
      description: 'Fetch course geometry, review difficult jumps, and optimize judging posts.',
      action: 'Continue to jumps',
      to: `${base}/jumps`,
    };
  }
  return {
    title: 'Complete judge assignments',
    description:
      'Place known judges first, then fill remaining posts and review uncovered packets.',
    action: 'Continue to assignments',
    to: `${base}/assignments`,
  };
}
